import React, { useState, useEffect } from 'react';
import { Check, Send, Loader2 } from 'lucide-react';
import { supabase } from '../config/supabaseClient';

function Input({ label, error, ...props }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">{label}</label>
      {props.as === 'textarea' ? (
        <textarea
          {...props}
          as={undefined}
          className={`w-full border-b ${error ? 'border-red-400' : 'border-gray-200'} focus:border-black outline-none py-2.5 text-sm resize-none bg-transparent transition-colors font-light`}
        />
      ) : (
        <input
          {...props}
          className={`w-full border-b ${error ? 'border-red-400' : 'border-gray-200'} focus:border-black outline-none py-2.5 text-sm bg-transparent transition-colors font-light`}
        />
      )}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(form.phone.replace(/\D/g, ''))) e.phone = 'Enter a valid 10-digit phone number';
    if (!form.subject.trim()) e.subject = 'Subject is required';
    if (!form.message.trim()) e.message = 'Message is required';
    else if (form.message.length < 10) e.message = 'Message must be at least 10 characters';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    setLoading(true);
    try {
      const { error } = await supabase.from('contact_messages').insert({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setErrors({ form: err.message || 'Could not send message. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen">
      {/* Header */}
      <section className="bg-[#fdf8f6] py-20 text-center">
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Get in Touch</p>
        <h1 className="font-display text-5xl sm:text-6xl font-light">Contact Us</h1>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
          {/* Info */}
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-6">How can we help?</p>
            <h2 className="font-display text-4xl font-light leading-snug mb-8">
              We'd love to<br />hear from you.
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed font-light mb-10">
              Whether you have questions about products, skincare routines, orders, collaborations,
              or general support, our team is here to help.
            </p>

            <div className="space-y-6">
              {[
                { label: 'Business Name', value: 'Metameni Labs Pvt Ltd' },
                {
                  label: 'Address',
                  value:
                    '4330, Nahar Kha Sarai Lalpul, Budaun, Uttar Pradesh, India – 243601',
                },
                { label: 'Phone', value: '+91 94120 64060' },
                { label: 'Email', value: 'hello@metameni.com' },
                { label: 'Website', value: 'www.metameni.com' },
                { label: 'Business Hours', value: 'Monday to Saturday, 10:00 AM – 7:00 PM IST' },
              ].map(item => (
                <div key={item.label}>
                  <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-1">{item.label}</p>
                  <p className="text-sm font-light">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 p-8 bg-[#fdf8f6]">
              <p className="font-display text-2xl font-light mb-3">Faster support</p>
              <p className="text-sm text-gray-500 font-light leading-relaxed mb-4">
                Please include your order ID while contacting us regarding existing orders.
              </p>
              <p className="text-sm text-gray-500 font-light leading-relaxed">
                Connect with us on Instagram, YouTube, and LinkedIn for skincare updates,
                educational content, launches, and community support.
              </p>
            </div>
          </div>

          {/* Form */}
          <div>
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mb-6">
                  <Check size={28} className="text-white" />
                </div>
                <h3 className="font-display text-3xl font-light mb-3">Message sent!</h3>
                <p className="text-gray-500 text-sm font-light max-w-xs">
                  Thank you for reaching out. We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                  className="mt-8 text-xs tracking-widest uppercase border-b border-black pb-0.5 hover:text-gray-500 transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-8">
                {errors.form && (
                  <p className="text-xs text-red-500 bg-red-50 border border-red-200 px-4 py-3 rounded">
                    {errors.form}
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Input
                    label="Full Name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                    error={errors.name}
                  />
                  <Input
                    label="Email Address"
                    name="email"
                    type="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={handleChange}
                    error={errors.email}
                  />
                </div>
                <Input
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={(e) => handleChange({
                    target: { name: 'phone', value: e.target.value.replace(/\D/g, '').slice(0, 10) }
                  })}
                  error={errors.phone}
                />
                <Input
                  label="Subject"
                  name="subject"
                  type="text"
                  placeholder="What's this about?"
                  value={form.subject}
                  onChange={handleChange}
                  error={errors.subject}
                />
                <div>
                  <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">Message</label>
                  <textarea
                    name="message"
                    placeholder="Tell us more..."
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    className={`w-full border-b ${errors.message ? 'border-red-400' : 'border-gray-200'} focus:border-black outline-none py-2.5 text-sm resize-none bg-transparent transition-colors font-light`}
                  />
                  {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 bg-black text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium hover:bg-gray-800 transition-colors disabled:opacity-60"
                >
                  {loading ? <><Loader2 size={13} className="animate-spin" /> Sending...</> : <><Send size={13} /> Send Message</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
